import React, { useReducer } from 'react';
import { Delete, RestoreFromTrash, UploadFile } from '@mui/icons-material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Button } from '@mui/material';
import { CardSection } from 'shared/components/cardControls';
import {
  useAuth, useLoading, useNotification, useRequest,
} from 'shared/hooks';
import { BoxWrapper, GridWrapper } from 'shared/components/data-display/GridFormatter';
import SimpleDataTable from 'shared/components/table/SimpleDataTable';
import { dateTimeString, FileSizeToString } from 'shared/utils';
import { BUTTON_COLORS, VARIANTS } from 'shared/constants';
import FileDropzone from 'shared/components/data-display/FileDropzone';
import {
  createFileUrl,
  deleteFile,
  getFileUrl,
  permanentlyDeleteFile,
  restoreDeletedFile,
  searchFiles,
} from 'features/support/api/files';
import { addEntry } from 'shared/api/support';
import { InfoModal, TooltipIconButton } from 'shared/components/abstract/interactables';

const initialState = {
  count: 0,
  pendingFiles: [],
  open: false,
};
function reducer(state, { action, payload }) {
  switch (action) {
    case 'pending':
      return { ...state, pendingFiles: payload };
    case 'toggle':
      return { ...state, open: !state.open, pendingFiles: [] };
    default:
      return state;
  }
}

/**
 * @param {Object} props
 * @param {boolean} props.disableLink
 * @param {number} props.id
 * @param {string} props.filename
 * @param {Function} props.request
 */
function viewFileLink(props) {
  const {
    disableLink,
    id,
    filename,
    request,
  } = props;
  if (disableLink) return filename;

  const onClick = async () => {
    const response = await request(getFileUrl(id));
    if (response) {
      window.open(response.secure_url, `${filename}`);
    }
  };
  return (
    <Button onClick={onClick}>{filename}</Button>
  );
}

/**
 * @param {Object} props
 * @param {Object} [props.deletedAt]
 * @param {Function} props.deleteFunc
 * @param {string} props.filename
 * @param {Function} props.restoreFunc
 */
function manageFileButton(props) {
  const {
    deletedAt,
    deleteFunc,
    filename,
    restoreFunc,
  } = props;

  if (deletedAt !== null) {
    return (
      <InfoModal
        className="save borderless-icon-btn"
        actionName="Restore"
        disabled={false}
        description={`Restore file ${filename}?`}
        icon={RestoreFromTrash}
        color={BUTTON_COLORS.success}
        tip="Restore file"
        actionCallback={restoreFunc}
        title="Restore File"
      />
    );
  }
  return (
    <InfoModal
      className="clear borderless-icon-btn"
      actionName="Delete"
      disabled={false}
      description={`Delete file ${filename}?`}
      icon={Delete}
      color={BUTTON_COLORS.error}
      tip="Delete file"
      actionCallback={deleteFunc}
      title="Delete File"
    />
  );
}

/**
 * @param {Object} props
 * @param {Object} props.ticketState
 * @param {Function} props.ticketDispatch
 */
export default function TicketFilesView(props) {
  const { user } = useAuth();
  const { setLoading } = useLoading();
  const { request } = useRequest();
  const notification = useNotification();
  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    ticketState: {
      data: { id },
      triggerReload,
    },
    ticketDispatch,
  } = props;

  const { open, pendingFiles } = state;

  const toggleOpen = (event, reason) => {
    // ensure dialog doesn't close/clear on backdrop clicks
    if (!reason || reason === 'escapeKeyDown') {
      dispatch({ action: 'toggle' });
    }
  };
  const setPendingFiles = (files) => dispatch({ action: 'pending', payload: files });

  async function deleteFunc(fileId, filename) {
    await request(deleteFile(fileId)).then(async (response) => {
      if (response) {
        await request(addEntry({
          text: `Deleted file ${filename}`,
          username: user.username,
          ticket_id: id,
          minutes: 0,
        }));
        notification.pushSuccess(`Successfully deleted file: ${filename}`);
        ticketDispatch({ action: 'reload' });
      } else {
        notification.pushError(`Encountered error deleting file: ${filename}`);
      }
    });
  }

  async function restoreFunc(fileId, file) {
    await request(restoreDeletedFile(fileId, file)).then(async (response) => {
      if (response) {
        await request(addEntry({
          text: `Restored file ${file.filename}`,
          username: user.username,
          ticket_id: id,
          minutes: 0,
        }));
        notification.pushSuccess(`Successfully restored ${file.filename}`);
        ticketDispatch({ action: 'reload' });
      } else {
        notification.pushError(`Encountered error restoring: ${file.filename}`);
      }
    });
  }

  const onSubmit = async () => {
    setLoading(true);
    await Promise.all(pendingFiles.map(
      (file) => request(createFileUrl({
        ticket_id: id,
        filename: file.name,
        size: file.size,
        file_type: file.type,
      })).then(async (response) => {
        if (response) {
          await fetch(response.secure_url, {
            method: 'PUT',
            body: file,
          })
            .then((result) => {
              if (result) {
                request(addEntry({
                  text: `Added File: ${file.name}`,
                  username: user.username,
                  ticket_id: id,
                  minutes: 0,
                }));
                notification.pushSuccess(`${file.name} successfully uploaded`);
              }
            }).catch(() => {
              notification.pushError(`Failed upload of ${file.name}`);
              request(permanentlyDeleteFile(response.id));
            });
        }
      }),
    )).finally(() => {
      setLoading(false);
      toggleOpen();
      ticketDispatch({ action: 'reload' });
    });
  };

  const columns = [
    {
      name: 'Name',
      formatter: (file) => viewFileLink({
        id: file.id,
        filename: file.filename,
        disableLink: file.deleted_at !== null,
        request,
      }),
    },
    {
      name: 'Type',
      jsonKey: 'file_type',
    },
    {
      name: 'Size',
      formatter: (file) => FileSizeToString(file.size),
    },
    {
      name: 'Upload Date',
      formatter: (file) => dateTimeString(file.created_at),
    },
    {
      name: 'Delete',
      formatter: (doc) => manageFileButton({
        deleteFunc: () => deleteFunc(doc.id, doc.filename),
        filename: doc.filename,
        deletedAt: doc.deleted_at,
        restoreFunc: () => restoreFunc(doc.id, doc),
      }),
    },
  ];

  return (
    <CardSection>
      <div className="card-controls-upload" style={{ display: 'flex' }}>
        <TooltipIconButton className="upload" message="Upload files" icon={UploadFile} onClick={toggleOpen} />
        <Dialog open={open} onClose={toggleOpen}>
          <DialogTitle>File Upload</DialogTitle>
          <DialogContent>
            <FileDropzone pendingFiles={pendingFiles} setPendingFiles={setPendingFiles} />
          </DialogContent>
          <DialogActions>
            <Button onClick={toggleOpen}>Cancel</Button>
            <Button
              variant={VARIANTS.contained}
              onClick={onSubmit}
              disabled={pendingFiles.length === 0}
            >
              Upload
            </Button>
          </DialogActions>
        </Dialog>
      </div>
      <BoxWrapper>
        <GridWrapper size={12} title="Files">
          <SimpleDataTable
            baseSearch={`ticket_id=${id}`}
            searchRequest={searchFiles}
            columns={columns}
            rowId="id"
            updateCount={triggerReload}
          />
        </GridWrapper>
      </BoxWrapper>
    </CardSection>
  );
}
